import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * COMPONENTE: PlaylistCriticalControls
 * 
 * Sección colapsable / acordeón inferior para los controles operativos críticos del estadio:
 * - Apertura / Pausa de recepción de sugerencias de aficionados.
 * - Reinicio total de la dinámica (borrado/archivado masivo a ceros con confirmación modal).
 */
export const PlaylistCriticalControls = ({
    settings = null,
    onToggleSubmission = () => {},
    onResetDynamic = () => {},
    isLoading = false
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const isSubmissionActive = settings ? settings.isSubmissionActive : true;

    return (
        <section className="PlaylistCriticalControls" aria-label="Controles críticos del estadio">
            {/* Header del acordeón colapsable */}
            <div
                className="PlaylistCriticalControls__Header"
                onClick={() => setIsExpanded(prev => !prev)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsExpanded(prev => !prev);
                    }
                }}
            >
                <div className="PlaylistCriticalControls__HeaderLeft">
                    <i className="bi bi-shield-lock-fill PlaylistCriticalControls__HeaderIcon"></i>
                    <div>
                        <h4 className="PlaylistCriticalControls__HeaderTitle">Controles Críticos del Estadio</h4>
                        <span className="PlaylistCriticalControls__HeaderSubtitle">
                            Apertura/Cierre de peticiones y reinicio de sala
                        </span>
                    </div>
                </div>

                <div className="PlaylistCriticalControls__HeaderRight">
                    {/* Badge de Estado de Recepción */}
                    <span className={`PlaylistCriticalControls__StatusBadge ${isSubmissionActive ? 'PlaylistCriticalControls__StatusBadge--Active' : 'PlaylistCriticalControls__StatusBadge--Paused'}`}>
                        <span className="PlaylistCriticalControls__StatusDot"></span>
                        {isSubmissionActive ? 'Recepción: Abierta' : 'Recepción: Pausada'}
                    </span>

                    {/* Botón / Icono de Expandir/Colapsar */}
                    <button
                        type="button"
                        className="PlaylistCriticalControls__ToggleBtn"
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Colapsar controles' : 'Expandir controles'}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(prev => !prev);
                        }}
                    >
                        <i className={`bi bi-chevron-down PlaylistCriticalControls__Chevron ${isExpanded ? 'PlaylistCriticalControls__Chevron--Rotated' : ''}`}></i>
                    </button>
                </div>
            </div>

            {/* Contenido colapsable */}
            {isExpanded && (
                <div className="PlaylistCriticalControls__Body">
                    <div className="row g-4">
                        {/* Control 1: Conmutador de Recepción de Sugerencias */}
                        <div className="col-12 col-md-6">
                            <div className="PlaylistCriticalControls__Card">
                                <div className="d-flex align-items-start justify-content-between gap-3">
                                    <div className="PlaylistCriticalControls__CardInfo">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <i className={`bi ${isSubmissionActive ? 'bi-broadcast-pin text-success' : 'bi-pause-circle text-warning'} fs-5`}></i>
                                            <h5 className="PlaylistCriticalControls__CardTitle mb-0">
                                                Recepción de Sugerencias
                                            </h5>
                                        </div>
                                        <p className="PlaylistCriticalControls__CardDesc mb-0">
                                            {isSubmissionActive
                                                ? 'Los aficionados en el estadio pueden sugerir canciones y votar en tiempo real.'
                                                : 'La recepción de peticiones está en pausa. Los aficionados verán mensaje de sala pausada.'}
                                        </p>
                                    </div>

                                    {/* Switch Toggle */}
                                    <div className="form-check form-switch PlaylistCriticalControls__SwitchWrapper">
                                        <input
                                            className="form-check-input PlaylistCriticalControls__SwitchInput"
                                            type="checkbox"
                                            role="switch"
                                            id="submissionActiveSwitch"
                                            checked={isSubmissionActive}
                                            onChange={onToggleSubmission}
                                            disabled={isLoading}
                                        />
                                        <label
                                            className="form-check-label visually-hidden"
                                            htmlFor="submissionActiveSwitch"
                                        >
                                            Conmutar recepción de canciones
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Control 2: Reinicio de Dinámica a Ceros */}
                        <div className="col-12 col-md-6">
                            <div className="PlaylistCriticalControls__Card PlaylistCriticalControls__Card--Danger">
                                <div className="d-flex align-items-start justify-content-between gap-3">
                                    <div className="PlaylistCriticalControls__CardInfo">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <i className="bi bi-exclamation-triangle-fill text-danger fs-5"></i>
                                            <h5 className="PlaylistCriticalControls__CardTitle text-danger mb-0">
                                                Reinicio de Dinámica a Ceros
                                            </h5>
                                        </div>
                                        <p className="PlaylistCriticalControls__CardDesc mb-0">
                                            Archiva todas las peticiones activas, detiene la reproducción en vivo y limpia la lista para el próximo partido.
                                        </p>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-danger PlaylistCriticalControls__DangerBtn flex-shrink-0"
                                        onClick={onResetDynamic}
                                        disabled={isLoading}
                                    >
                                        <i className="bi bi-arrow-counterclockwise me-1"></i>
                                        Reiniciar Sala
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

PlaylistCriticalControls.propTypes = {
    settings: PropTypes.object,
    onToggleSubmission: PropTypes.func,
    onResetDynamic: PropTypes.func,
    isLoading: PropTypes.bool
};

export default PlaylistCriticalControls;
